<template>
  <div class="configurator-step-email">
    <div class="configurator-step-email-teaser">
      <h3>Dein Fahrzeug ist bereit zur Preisbewertung</h3>
      <div class="table-wraper">
        <table cellspacing="0" cellpadding="0">
          <tr>
            <td><b>Marke</b> {{ selected("make") }}</td>
            <td><b>Modell</b> {{ selected("model") }}</td>
            <td><b>Erstzulassung</b> {{ selected("month", true) }} / {{ selected("year") }}</td>
            <td><b>Fahrzeugtyp</b> {{ selected("bodytype") }}</td>
          </tr>
          <tr>
            <td><b>Kraftstoff</b> {{ selected("fuel") }}</td>
            <td><b>Leistung</b> {{ selected("power") }}</td>
            <td><b>Motor</b> {{ selected("engine") }}</td>
            <td><b>Variante</b> {{ selected("equipment") }}</td>
          </tr>
          <tr>
            <td colspan="4"><b>Extras</b> {{ checked("extras") }}</td>
          </tr>
          <tr>
            <td colspan="4"><b>Kilometerstand</b> {{ entered("mileage") }}</td>
          </tr>
        </table>
      </div>
    </div>
    <div class="configurator-step-email-content">
      <p v-if="!signinError">
        Du erhältst im nächsten Schritt deine kostenlose &amp; faire Preisbewertung! Wenn du noch kein Kundenkonto hast, senden wir dir eine E-Mail zur  Verifizierung deiner E-Mail-Adresse zu.
      </p>
       <p v-if="signinError">
        Du erhältst im nächsten Schritt deine kostenlose & faire Preisbewertung!
       </p>

      <p class="unautorized-error" v-if="emailError">
        Für diese E-Mail-Adresse existiert bereits ein PKW.de Konto. Bitte melde dich an, um die Preisbewertung zu erhalten. Solltest du dein Passwort vergessen haben, so kannst du es <a href="https://suche.pkw.de/passwort/editieren" target="_blank">hier zurücksetzen</a>.
      </p>

      <label for="email">
        E-Mail Adresse
        <input ref="email" name="email" v-bind:class="{ 'with-error': emailError, 'email-not-valid': email && invalidEmail(), 'email-is-valid': email && !invalidEmail() }" v-model="email" type="email" tabindex="-1" />
      </label>
      <p v-if="validationError" class="message-error">{{ this.validationDetails }}</p>
      <p v-if="signinError" class="message-error">Diese E-Mail-Adresse wurde noch nicht bestätigt. Wir haben dir erneut eine E-Mail zur Verifizierung deiner E-Mail-Adresse zugesendet. Du erhältst die Fahrzeugbewertung direkt nach der Bestätigung.</p>
      <p v-if="dealerEmailError" class="message-error">Achtung! Diese E-Mail-Adresse ist mit einem Händlerkonto verbunden. Bitte verwenden Sie eine andere E-Mail, um das neue Konto zu erstellen.</p>

      <label for="password" v-if="emailError" v-bind:class="{ 'with-error-psw': passwordError }">
        Passwort
        <input name="password" v-model="password" type="password" tabindex="-1"
          v-bind:class='{ "psw-error-input": passwordError }' />
      </label>
      <p v-if="passwordError" class="message-error">{{ this.passwordErrorMessage }}</p>

      <p class="disclaimer">
        Mit der Übermittlung deiner E-Mail-Adresse stimmst du den PKW.de <a href="http://www.pkw.de/pages/datenschutz/" target="blank" tabindex="-1">Datenschutzbestimmungen</a> und dem Erhalt von E-Mails mit der Fahrzeugbewertung und Verkaufsmöglichkeiten zu. Du kannst deine Zustimmung jederzeit mit Wirkung für die Zukunft widerrufen.
      </p>

      <a @click.prevent="submit" :disabled="isDisabled" :class="isDisabled && 'disabled'" class="submit"
        title="Preisbewertung erhalten" v-wave="{ color: '#54a324', initialOpacity: 0.3, easing: 'ease-in' }">Preisbewertung
        erhalten</a>
    </div>
  </div>
</template>

<script>
import { sendLead, sendValuation, makeLogin } from '../services/api-helpers';
import { isStagingEnv, getSucheLink } from '../services/helpers';

function trackEvent(name, value) {
  console.log(`GTAG tracked > ${name} ${value}`);
      
  dataLayer.push({ name: value });
};

export default {
  name: 'ConfiguratorStepEmail',
  props: ['name', 'options'],
  data: function () {
    return {
      isLoading: false,
      email: null,
      emailError: false,
      password: null,
      passwordError: false,
      passwordErrorMessage: '',
      signinError: false,
      dealerEmailError: false,
      isTracked: false,
      validationError: false,
      validationDetails: ''
    }
  },
  methods: {
    invalidEmail: function() {
      var tester = /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
      return this.email.match(tester) === null;
    },
    submit: async function (event) {
      if (this.emailError) {
        const isValidPassword = this.passwordValidation();
        if (!isValidPassword) { return };
        
        try {
          this.isLoading = true;
          const { data: { token: loginToken } } = await makeLogin(this.email, this.password);
          const { data: valuation } = await sendValuation(this.$store.state.selected);
          // this is needed to make shure that the extras property is no overwritten by the valuation response
          await sendLead({ ...this.$store.state.selected, ...valuation, extras: this.$store.state.selected.extras }, loginToken);

          trackEvent('car_evaluation_step', 'RU-2');
          await this.redirectToProfile(loginToken)
        } catch (e) {
          console.log('error', e);
          this.wrongPassword(e);
        } finally {
          this.isLoading = false;
        }
      } else {
        this.$store.commit('updateSelected', { name: 'email', value: this.email });
        this.sendNewLead();
      }
    },
    redirectToModal: function (email) {
      const isStaging = isStagingEnv();
      const url = getSucheLink(isStaging);

      window.location.href = `${url}/widget-registration`;
    },
    redirectToProfile: function (token) {
      const isStaging = isStagingEnv();
      const url = getSucheLink(isStaging);

      window.location.href = `${url}/widget-registration?userToken=${token}`;
    },
    entered: function (param) {
      return this.$store.state.selected[param];
    },
    selected: function (param, getId = false) {
      if (this.$store.state.selected[param]) {
        let id = this.$store.state.selected[param];
        let item = this.$store.state.options[param].find(function (element) {
          return element.id == id;
        });
        if (getId) return id;
        return item.name;
      } else {
        return 'keine';
      }
    },
    checked: function (param) {
      if (this.$store.state.selected[param]) {
        let ids = this.$store.state.selected[param];
        let items = this.$store.state.options[param].filter(function (element) {
          return ids.indexOf(element.id) >= 0;
        });
        return items.map(x => x.name).join(", ");
      } else {
        return 'keine';
      }
    },
    sendNewLead: async function () {
      const valuation = await sendValuation(this.$store.state.selected);
      try {
        // this is needed to make shure that the extras property is no overwritten by the valuation response
        await sendLead({ ...this.$store.state.selected, ...valuation.data, extras: this.$store.state.selected.extras })
        trackEvent('car_evaluation_step', 'NU-1');
        await this.redirectToModal(this.email);
      } catch (e) {
        this.validationErrors(e);
        this.unautorizedUser(e);
        this.unconfirmedEmail(e);
        this.handleDealerEmail(e);
      }
    },
    validationErrors: function (error) {
      if (error.response.data.error === "validation_errors") {
        this.validationError = true;
        this.validationDetails = error.response.data.details;
      }
    },
    unautorizedUser: function (error) {
      if (error.response.data.error === "unauthorized") {
        this.emailError = true;
        this.signinError = false;
        this.dealerEmailError = false;

        if (!this.isTracked) {
          this.isTracked = true;
          trackEvent('car_evaluation_step', 'RU-1');
        }
      }
    },
    unconfirmedEmail: function (error) {
      if (error.response.data.error === "signin_required") {
        this.signinError = true;
        this.dealerEmailError = false;
      }
    },
    // We don't allow to use e-mail address, which is associated with a dealer account
    handleDealerEmail: function (error) {
      if (error.response.data.error === "user_is_dealer") {
        this.dealerEmailError = true;
        this.signinError = false;
      }
    },
    wrongPassword: function (error) {
      if (error.response.data.error === "wrong_credentials") {
        this.passwordError = true;
        this.passwordErrorMessage = 'Passwort falsch eingegeben';
      }
    },
    passwordValidation: function () {
      const isValidPassword = this.password !== null && this.password.length > 0;
      if (!isValidPassword) {
        this.passwordError = true;
        this.passwordErrorMessage = 'Passwort eingeben';
      }

      return isValidPassword;
    },
    resetErrors() {
      this.password = null;
      this.emailError = false;
      this.passwordError = false;
      this.passwordErrorMessage = '';
      this.signinError = false;
      this.dealerEmailError = false;
      this.validationError = false;
      this.validationDetails = '';
    }
  },
  computed: {
    isDisabled: function () {
      return this.email == null || this.email.length == 0 || this.invalidEmail() || this.isLoading;
    },
    isFirstStep: function () {
      return this.$store.state.step === "make";
    },
    isActive () {
      return this.$attrs.isActive;
    }
  },
   watch: {
    isFirstStep (condition) {
      if (condition) {
        this.email = null;
        this.password = null;
        this.emailError = false;
        this.passwordError = false;
        this.passwordErrorMessage = '';
        this.signinError = false;
        this.dealerEmailError = false;
      }
    },
    isActive (isActive) {
      if (isActive) {
        trackEvent('car_evaluation_step', '0');
      }
    },
    email (newEmail, prevEmail) {
      
      if (prevEmail !== newEmail) {
        this.resetErrors();
        // Focus the email input here
        this.$nextTick(() => {
          this.$refs.email.focus();
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@/assets/vars";
@import "~@/assets/colors";

.configurator-step-email-content {
  height: 100%;
  float: left;
  padding: 30px 20px 0 20px;

  @include breakpoint(frommediumlarge) {
    width: 50%;
  }
}

.configurator-step-email-teaser {
  height: 500px;
  float: left;
  padding: 15px 0 20px 35px;

  @include breakpoint(frommediumlarge) {
    width: 50%;
  }
}

.configurator-step-email-teaser h3 {
  margin: 0;
  padding: 0;
  color: #54a324;
  font-size:18px;
  line-height:1.2;
  font-weight: 400;
}

.configurator-step-email-teaser h3:after {
  content: "\E5CA";
  font-family: "Material Icons";
  font-size: 16px;
  line-height: 32px;
  position: absolute;
  left: -25px;
  top: -6px;
}

.unautorized-error {
  background-color: #c0181e21;
  margin: 15px;
  padding: 10px;
}

.message-error {
  color: $red;
  display: inline-block;
  padding-top: 0;
  font-size: 12px;
}

.psw-error-input {
  outline: 1px solid red;
}

table {
  width: 100%;
  margin-top: 20px;
  padding: 15px;
  background: #fff;
  border-radius: 3px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.15),
    0px 5px 50px 1px rgba(0, 0, 0, 0.05);
}

table:after {
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  border-radius: 3px;
  content: '';
  pointer-events: none;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

table td {
  padding: 10px 15px;
  font-size: 12px;
  width: 25%;
  vertical-align: top;
  line-height: 15px;
}

table tr:first-child td {
  // padding-top: 0;
}

table b {
  color: #aaa;
  font-weight: normal;
  display: block;
  margin-bottom: 5px;
}

p {
  margin: 0;
  padding: 20px 20px 0 20px;
  font-size: 14px;
  line-height: 20px;
}

p.disclaimer {
  font-size: 11px;
  line-height: 17px;
}

p.disclaimer a {
  border-bottom: 1px solid $black;
  text-decoration: none;
  color: $black;
}

label {
  margin: 20px 20px 0 20px;
  display: block;
  font-size: 12px;
  font-weight: 600;
}

input[type="text"],
input[type="email"],
input[type="password"] {
  border: 1px solid #dadada;
  border-radius: 3px;
  padding: 0 10px;
  line-height: 40px;
  display: block;
  font-size: 14px;
  width: 100%;
  margin-top: 5px;
  height: 50px;

  transition: border-color .2s;
}

input[type="text"]:focus,
input[type="email"]:focus {
  outline: none;
}

.email-not-valid {
  border-color: $orange !important;
}
.email-is-valid {
  border-color: $widget-green !important;
}

.submit {
  height: auto;
  width: auto;
  font-size: 14px;
  line-height: 20px;
  display: block;
  border: 1px solid #54a324;
  color: #54a324;
  background: transparent;
  border-radius: 23px;
  padding: 10px;
  text-align: center;
  font-weight: 600;
  cursor: pointer;
  margin: 20px;
  transition: opacity .2s;

  @media (hover: hover) {
    &:not(.disabled):hover {
      text-decoration: none;
      background-color: #eee;
    }
  }

  @include breakpoint(frommediumlarge) {
    margin: 20px;
  }
}

.submit:focus {
  outline: none;
}

.submit.disabled {
  pointer-events: none;
  opacity: 0.25;
  cursor: default;
}

.configurator-step-email-content-intro {
  font-size: 12px;
  font-weight: 600;
  padding-bottom: 10px;
  display: block;
}

#motor-verkauf {
  max-height: 30px;
  margin-top: 10px;
  padding-left: 0;
  display: block;
  max-width: 100%;
}

@include breakpoint(medium) {

  .configurator-step-email-teaser,
  .configurator-step-email-content {
    width: 100%;
    height: auto;
    float: none;
    padding: 0 10px;
    margin: 0 auto;
  }

  .configurator-step-email-teaser {
    display: none;
  }
}

@include breakpoint(frommedium) {

  .configurator-step-email-teaser,
  .configurator-step-email-content {
    width: 50%;
  }
}


@include breakpoint(small) {
  .configurator-step-email {
    overflow-y: scroll;
    position: absolute;
    top: 62px;
    left: 0;
    right: 0;
    bottom: 0;
    margin: 0;
  }

  .configurator-step-email-teaser,
  .configurator-step-email-content {
    width: auto;
    height: auto;
    float: none;
    padding: 0 10px 30px;
  }

  .configurator-step-email-teaser {
    display: none;
  }

  .submit {
    width: auto;
    display: block;
  }
}

// @include breakpoint(small) {
//   .configurator-step-email {
//     overflow-y: scroll;
//     position: absolute;
//     top: 62px;
//     left: 0;
//     right: 0;
//     bottom: 0;
//     margin: 0;
//   }
// }
</style>



// WEBPACK FOOTER //
// src/components/ConfiguratorStepEmail.vue